import { useIntl } from 'react-intl';
import { SchemaDatapointEnumOptions } from '@rossum/api-client/schemas';
import { IconCustomMenuDown } from '@rossum/ui/icons/tabler';
import {
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import {
  GridRenderCellParams,
  GridRowId,
  useGridApiContext,
} from '@rossum/ui/x-data-grid-pro';
import { DatapointColumn, RowModel } from '../rows/helpers';
import { CellIcons } from './CellIcons';
import { CellValueSkeleton } from './CellValueSkeleton';
import { OptionValue } from './OptionValue';
import { StructBadge } from './StructBadge';
import {
  resolveAutomationBlockersPerDatapoints,
  resolveMessagesPerDatapoints,
} from './utils';

export const formulaPreviewClassName = 'current-formula-preview-cell';

const DatapointCell = ({
  value,
  schema,
  editable,
  id,
  isCurrentSchema,
  isUnusedDependency,
}: {
  value: NonNullable<RowModel[DatapointColumn]>;
  schema?: { options?: SchemaDatapointEnumOptions; type: string };
  editable?: boolean;
  id: GridRowId;
  isCurrentSchema?: boolean;
  isUnusedDependency?: boolean;
}) => {
  const api = useGridApiContext();
  const intl = useIntl();
  const [first, ...rest] = value.datapoints;
  const restLength = rest.length;

  // Options from datapoint
  const options =
    first?.category === 'datapoint' && first.options ? first.options : [];

  const datapointValue =
    first && 'content' in first && first.content
      ? first.content.value
      : undefined;

  const datapointStruct =
    first && 'content' in first && first.content?.struct != null
      ? first.content.struct
      : null;

  const displayValue = value.preview || datapointValue;

  const option =
    options?.find(option => option.value === displayValue) ??
    schema?.options?.find(option => option.value === displayValue);

  const resolvedMessages = resolveMessagesPerDatapoints(value);

  const resolvedAutomationBlockers =
    resolveAutomationBlockersPerDatapoints(value);

  const hasOptions = schema?.type === 'enum';

  const cellContent = (
    <Stack
      component="span"
      direction="row"
      sx={{
        [`.${formulaPreviewClassName} &`]: {
          color: 'success.main',
        },
        ...(isUnusedDependency && {
          opacity: 0.5,
        }),
      }}
      spacing={1}
      alignItems="center"
      width="100%"
      height="100%"
    >
      <CellIcons
        type={null}
        messages={resolvedMessages}
        automationBlockers={resolvedAutomationBlockers}
      />
      {restLength ? (
        <Typography variant="body2" component="span" color="text.secondary">
          {intl.formatMessage(
            {
              id: 'features.queueSettings.fields.formulaPreview.table.moreLines',
            },
            { count: restLength }
          )}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          component="span"
          whiteSpace="preserve"
          width="100%"
          height="100%"
          alignContent="center"
          sx={{
            cursor: editable ? 'pointer' : undefined,
          }}
          onClick={() => {
            if (editable) {
              api.current.startRowEditMode({
                id,
              });
            }
          }}
        >
          {editable && !value.preview ? (
            <TextField
              fullWidth
              slotProps={{
                htmlInput: {
                  sx: {
                    fontSize: theme => theme.typography.body2.fontSize,
                  },
                },
              }}
              onFocus={() => {
                api.current.startRowEditMode({
                  id,
                });
              }}
              size="small"
            />
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {isCurrentSchema && <StructBadge struct={datapointStruct} />}
              {(option && hasOptions ? (
                <OptionValue {...option} />
              ) : undefined) ||
                (hasOptions ? (
                  <OptionValue label="-" value={displayValue || ' '} />
                ) : undefined) ||
                (displayValue ? (
                  <Typography variant="body2">{displayValue}</Typography>
                ) : (
                  ' '
                ))}
              {isCurrentSchema && options && options.length > 1 ? (
                <Tooltip
                  title={
                    <Stack spacing={0.5}>
                      {options.map(o => (
                        <Stack key={o.value} spacing={0.5} textAlign="left">
                          <OptionValue {...o} />
                        </Stack>
                      ))}
                    </Stack>
                  }
                >
                  <SvgIcon fontSize="large" sx={{ color: 'text.secondary' }}>
                    <IconCustomMenuDown />
                  </SvgIcon>
                </Tooltip>
              ) : null}
            </Stack>
          )}
        </Typography>
      )}
    </Stack>
  );

  if (isUnusedDependency) {
    const unusedTooltipText = intl.formatMessage({
      id: 'features.queueSettings.fields.formulaPreview.table.dependencyNotUsed',
    });
    return <Tooltip title={unusedTooltipText}>{cellContent}</Tooltip>;
  }

  return cellContent;
};

export const DatapointCellRenderer = ({
  params,
  schema,
  isCurrentSchema,
  isUnusedDependency,
}: {
  params: GridRenderCellParams<RowModel, RowModel[DatapointColumn]>;
  schema?: { options?: SchemaDatapointEnumOptions; type: string };
  isCurrentSchema?: boolean;
  isUnusedDependency?: boolean;
}) => {
  return params.value === undefined || params.row.isFetching ? (
    <CellValueSkeleton />
  ) : (
    <DatapointCell
      id={params.id}
      value={params.value}
      schema={schema}
      editable={params.isEditable === true}
      isCurrentSchema={isCurrentSchema}
      isUnusedDependency={isUnusedDependency}
    />
  );
};
